/**
 * Wudy Kitchen Journal V3 — Vanilla JS
 * Kinfolk-replica interactions:
 *  - IntersectionObserver: clip-path image reveals, heading reveals, fade-ups
 *  - Parallax: scroll-driven translateY for .jv3-spin__bg
 *  - Sticky sidebar: active state tracking via scroll
 *  - Latest stories panel: category arrow navigation
 *  - Hero: initial load animation sequence
 */

(function () {
  'use strict';

  function isJournalMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function prefersSmoothScroll() {
    return (
      window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* ── 1. IntersectionObserver — reveal animations ── */
  function initRevealObserver() {
    // Skip all animations if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isJournalMobile()) {
      document.querySelectorAll(
        '.jv3-card__img, .jv3-card__category, .jv3-card__title, .jv3-card__deck,' +
        '.jv3-hero__title, .jv3-hero__deck,' +
        '.jv3-spin__title, .jv3-spin__deck, .jv3-spin__eyebrow,' +
        '.jv3-issue__cover, .jv3-reveal,' +
        '.jv3-issue-cover__inner, .jv3-spin__inner, .jv3-stories-grid__inner'
      ).forEach(function(el) { el.classList.add('is-revealed'); });
      return;
    }
    const targets = document.querySelectorAll(
      '.jv3-card__img, .jv3-card__category, .jv3-card__title, .jv3-card__deck,' +
      '.jv3-hero__title, .jv3-hero__deck,' +
      '.jv3-spin__title, .jv3-spin__deck, .jv3-spin__eyebrow,' +
      '.jv3-issue__cover,' +
      '.jv3-issue-cover__inner, .jv3-spin__inner, .jv3-stories-grid__inner,' +
      '.jv3-reveal'
    );

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    targets.forEach(function (el) { observer.observe(el); });

    // Fallback: reveal all remaining after 2s (catches slow IO or headless envs)
    setTimeout(function () {
      targets.forEach(function (el) {
        if (!el.classList.contains('is-revealed')) {
          el.classList.add('is-revealed');
        }
      });
    }, 2000);
  }

  /* ── 2. Hero: stable above-the-fold text (no delayed reveal — avoids CLS) ── */
  function initHeroLoad() {
    var hero = document.querySelector('.jv3-hero');
    if (!hero) return;

    var title = hero.querySelector('.jv3-hero__title');
    var deck = hero.querySelector('.jv3-hero__deck');
    if (title) title.classList.add('is-revealed');
    if (deck) deck.classList.add('is-revealed');
  }

  /* ── 3. Parallax: scroll-driven translateY for spin cycle bg ── */
  function initParallax() {
    if (isJournalMobile()) return;
    var spinSection = document.querySelector('.jv3-spin');
    var spinBg = document.querySelector('.jv3-spin__media, .jv3-spin__bg');
    if (!spinSection || !spinBg) return;

    var ticking = false;

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(function () {
          var rect = spinSection.getBoundingClientRect();
          var viewH = window.innerHeight;
          // Parallax factor 0.3: bg moves at 30% of scroll speed
          var progress = (viewH - rect.top) / (viewH + rect.height);
          var strength = parseInt(spinSection.dataset.parallaxStrength || '120', 10);
    var shift = (progress - 0.5) * -(strength); // px, dynamically set by schema
          spinBg.style.transform = 'translateY(' + shift + 'px)';
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // initial position
  }

  /* ── 4. Sticky sidebar: active category by scroll position ── */
  function initStickyCategories() {
    var cats = document.querySelectorAll('.jv3-sidebar__cat');
    var articleGroups = document.querySelectorAll('.jv3-cat-group');
    if (!cats.length || !articleGroups.length) return;

    function getActiveGroup() {
      var scrollY = window.scrollY + window.innerHeight * 0.4;
      var active = null;
      articleGroups.forEach(function (g) {
        var top = g.getBoundingClientRect().top + window.scrollY;
        if (scrollY >= top) active = g.dataset.cat;
      });
      return active;
    }

    var sidebarTicking = false;
    function updateSidebar() {
      var activeCat = getActiveGroup();
      cats.forEach(function (cat) {
        if (activeCat && cat.dataset.cat === activeCat) {
          cat.classList.add('is-active');
        } else {
          cat.classList.remove('is-active');
        }
      });
    }

    function onSidebarScroll() {
      if (sidebarTicking) return;
      sidebarTicking = true;
      requestAnimationFrame(function () {
        sidebarTicking = false;
        updateSidebar();
      });
    }

    // Click to scroll to category
    cats.forEach(function (cat) {
      cat.addEventListener('click', function () {
        var target = document.querySelector('.jv3-cat-group[data-cat="' + cat.dataset.cat + '"]');
        if (target) {
          target.scrollIntoView({
            behavior: prefersSmoothScroll() ? 'smooth' : 'instant',
            block: 'start',
          });
        }
      });
    });

    window.addEventListener('scroll', onSidebarScroll, { passive: true });
    updateSidebar();
  }

  /* ── 5. Latest Stories panel: decorative rotation only for static spans ── */
  function initLatestStories() {
    document.querySelectorAll('.jv3-latest').forEach(function (panel) {
      if (panel.querySelector('a.jv3-latest__cat-item, a.jv3-latest__title-item')) {
        return;
      }

      var catItems = panel.querySelectorAll('.jv3-latest__cat-item');
      var titleItems = panel.querySelectorAll('.jv3-latest__title-item');
      if (!catItems.length) return;

      var current = 0;
      var total = catItems.length;

      function setActive(idx) {
        catItems.forEach(function (el, i) {
          el.classList.toggle('is-active', i === idx);
        });
        titleItems.forEach(function (el, i) {
          el.classList.toggle('is-active', i === idx);
        });
      }

      setActive(0);

      setInterval(function () {
        current = (current + 1) % total;
        setActive(current);
      }, 3000);

      catItems.forEach(function (el, i) {
        el.addEventListener('mouseenter', function () { setActive(i); });
      });
    });
  }

  /* ── 6. Spin cycle: trigger reveal on scroll into view ── */
  function initSpinReveal() {
    var spinSection = document.querySelector('.jv3-spin');
    if (!spinSection) return;

    var observer = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) {
          var eyebrow = spinSection.querySelector('.jv3-spin__eyebrow');
          var title = spinSection.querySelector('.jv3-spin__title');
          var deck = spinSection.querySelector('.jv3-spin__deck');

          setTimeout(function () { if (eyebrow) eyebrow.classList.add('is-revealed'); }, 100);
          setTimeout(function () { if (title) title.classList.add('is-revealed'); }, 300);
          setTimeout(function () { if (deck) deck.classList.add('is-revealed'); }, 600);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(spinSection);
  }

  /* ── 7. JSON-LD schema injection for each article card ── */
  function initArticleSchema() {
    var schemaEls = document.querySelectorAll('[data-article-schema]');
    schemaEls.forEach(function (el) {
      try {
        var data = JSON.parse(el.dataset.articleSchema);
        var script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          'headline': data.title,
          'description': data.deck,
          'image': data.image,
          'author': { '@type': 'Organization', 'name': 'Wudy Kitchen' },
          'publisher': {
            '@type': 'Organization',
            'name': 'Wudy Kitchen',
            'logo': { '@type': 'ImageObject', 'url': 'https://wudykitchen.com/logo.png' }
          },
          'datePublished': '2026-05-24',
          'dateModified': '2026-05-24',
          'mainEntityOfPage': {
            '@type': 'WebPage',
            '@id': 'https://wudykitchen.com/blogs/wudy-journal/' + data.slug
          },
          'url': 'https://wudykitchen.com/blogs/wudy-journal/' + data.slug,
          'keywords': data.category
        });
        document.head.appendChild(script);
      } catch (e) {
        // silently ignore malformed schema data
      }
    });
  }

  /* ── 8. Smooth anchor navigation within page ── */
  function initSmoothAnchors() {
    document.querySelectorAll('a[href^="#"]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        var target = document.querySelector(el.getAttribute('href'));
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: prefersSmoothScroll() ? 'smooth' : 'instant',
            block: 'start',
          });
        }
      });
    });
  }

  /* ── Init all ── */
  function init() {
    initRevealObserver();
    initHeroLoad();
    initParallax();
    initStickyCategories();
    initLatestStories();
    initSpinReveal();
    initArticleSchema();
    initSmoothAnchors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
